import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { PATH } from 'common/constants';
import { useHasPermissions } from 'hooks/useHasPermissions';
import { NotStartedBanner } from './NotStartedBanner';
import { InProgressBanner } from './InProgressBanner';
import { MinimizedBanner } from './MinimizedBanner';
import { HideBannersModal } from './HideBannersModal';
import cn from './styles.module.scss';
import { PermissionCode, SurveySeason, SurveySeasonService, UserService } from 'types';
import { showNotification } from 'common/function';
import { isApiError } from 'common/types';
import { reportError } from 'utils/reporting';

export { SurveySeasonBadge } from './SurveySeasonBadge';

type SurveySeasonBannerProps = {
  /** Settings > Plans shows a minimized banner instead of hiding it entirely. */
  minimizeWhenHidden?: boolean;
};

const SRW_BANNERS_HIDDEN_KEY = 'app:srwBannersHidden';

/**
 * Per-user dismissal. `surveySeasonId` is the season open when the user hid the
 * banners, or `null` for a not-started dismissal (no season yet).
 */
type SrwBannersHiddenSetting = { surveySeasonId: number | null; hiddenAt: string } | null;

export function SurveySeasonBanner({ minimizeWhenHidden = false }: SurveySeasonBannerProps) {
  const router = useRouter();
  // Gates every entry point at once — Dashboard, Plans and Surveys all render this banner.
  const { permitted, checking } = useHasPermissions({
    permissions: [PermissionCode.MANAGE_PLANS, PermissionCode.VIEW_SURVEY_SEASON_WORKFLOW],
  });
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [surveySeason, setSurveySeason] = useState<SurveySeason | undefined>()
  const [bannersHiddenSetting, setBannersHiddenSetting] = useState<SrwBannersHiddenSetting>(null)
  const [confirmingHide, setConfirmingHide] = useState<boolean>(false)

  useEffect(() => {
    if (checking || !permitted) {
      return;
    }
    (async () => {
      setIsLoading(true);
      // Settled independently — a failure in one fetch must not discard an
      // already-resolved result from the other (see catch blocks below).
      const [seasonResult, settingsResult] = await Promise.allSettled([
        SurveySeasonService.getSurveySeason(),
        UserService.getUserSettings({ requestBody: { keys: [SRW_BANNERS_HIDDEN_KEY] } }),
      ]);

      if (seasonResult.status === 'fulfilled') {
        setSurveySeason(seasonResult.value);
      } else {
        const e = seasonResult.reason;
        // 404 means no survey season exists yet for this tenant — the expected
        // "not started" state, so surveySeason stays undefined and NotStartedBanner renders.
        if (!isApiError(e) || e.status !== 404) {
          reportError(e);
          showNotification(
            isApiError(e) ? e.message : 'Something went wrong',
            'Failed to load survey season',
            'error',
          )
        }
      }

      if (settingsResult.status === 'fulfilled') {
        setBannersHiddenSetting(settingsResult.value[SRW_BANNERS_HIDDEN_KEY] ?? null);
      } else {
        reportError(settingsResult.reason);
        showNotification(
          isApiError(settingsResult.reason) ? settingsResult.reason.message : 'Something went wrong',
          'Failed to load banner settings',
          'error',
        )
      }

      setIsLoading(false);
    })();
  }, [checking, permitted])

  // Hidden only if the dismissal was recorded for this exact state — a season id for
  // an in-progress season, or null for a not-started dismissal. A new season (new
  // surveySeason.id) or a season starting after a not-started dismissal always shows
  // the banner again, even if never explicitly restored.
  const bannersHidden = bannersHiddenSetting !== null
    && bannersHiddenSetting.surveySeasonId === (surveySeason?.id ?? null);

  const onAction = useCallback(() => {
    router.push(PATH.SURVEY_RESULTS_SEASON);
  }, [router]);

  const updateBannersHidden = useCallback(async (hide: boolean): Promise<boolean> => {
    const value: SrwBannersHiddenSetting = hide
      ? { surveySeasonId: surveySeason?.id ?? null, hiddenAt: new Date().toISOString() }
      : null;
    try {
      await UserService.upsertUserSetting({ requestBody: { key: SRW_BANNERS_HIDDEN_KEY, value } });
      setBannersHiddenSetting(value);
      return true;
    } catch (e) {
      reportError(e);
      showNotification(
        isApiError(e) ? e.message : 'Something went wrong',
        'Failed to update the Survey Refresh Workflow banners',
        'error',
      )
      return false;
    }
  }, [surveySeason]);

  const onConfirmHide = useCallback(async () => {
    if (await updateBannersHidden(true)) {
      setConfirmingHide(false);
    }
  }, [updateBannersHidden]);

  const onShowBanners = useCallback(() => {
    updateBannersHidden(false);
  }, [updateBannersHidden]);

  // Acting from the minimized card — Continue for a season, Get Started without one —
  // is itself a re-engagement with the plan, so it restores the full banners everywhere
  // too, not just on this page.
  const onActionFromMinimized = useCallback(async () => {
    if (await updateBannersHidden(false)) {
      onAction();
    }
  }, [updateBannersHidden, onAction]);

  // A completed season is the record that the workflow ran: no banner until a new one opens.
  if (checking || !permitted || isLoading || surveySeason?.completedAt) {
    return null;
  }

  if (bannersHidden) {
    if (!minimizeWhenHidden) {
      return null;
    }
    return (
      <div className={cn.minimizedBanner} data-testid="surveySeasonMinimizedBanner">
        <MinimizedBanner surveySeason={surveySeason} onAction={onActionFromMinimized} onShow={onShowBanners} />
      </div>
    );
  }

  return (
    <div className={cn.banner} data-testid="surveySeasonBanner">
      {surveySeason ? (
        <InProgressBanner
          surveySeason={surveySeason}
          onContinue={onAction}
          onClose={() => setConfirmingHide(true)}
        />
      ) : (
        <NotStartedBanner onGetStarted={onAction} onClose={() => setConfirmingHide(true)} />
      )}
      <HideBannersModal
        open={confirmingHide}
        onConfirm={onConfirmHide}
        onCancel={() => setConfirmingHide(false)}
      />
    </div>
  );
}
